import type { HsGeometryDownloadGroup } from '@sibe/hyperscene';
import { hsCountGeometryDownloadBatches, type HsFrustumSceneState } from './frustum-scene-state.class';

export type HsFrustumDownloadKind = 'meshes' | 'lines' | 'pointClouds';

/** Batch sizes for Frustum geometry downloads, in geometries unless noted. */
export interface HsFrustumDownloadSettings {
  downloadBatchSize: number;
  /** Textured meshes are large; they get a smaller fixed batch. */
  texturedMeshDownloadBatchSize: number;
  pointCloudDownloadBatchSize: number;
  /** When set, untextured mesh batches grow until they reach about this many bytes. */
  adaptiveMeshBatchBytes?: number;
  /**
   * When set, point cloud batches start at `pointCloudFirstBatchSize` geometries (default 4) and grow to about
   * this many bytes, at most `pointCloudDownloadBatchSize` geometries. With the point budget LOD the server lists
   * blocks nearest first, so small first batches show near surfaces before the far blocks arrive.
   */
  pointCloudBatchBytes?: number;
  pointCloudFirstBatchSize?: number;
}

/** Missing geometry ids of one Frustum delta, per kind. */
export interface HsFrustumMissingGeometry {
  meshes: readonly string[];
  lines: readonly string[];
  pointClouds: readonly string[];
}

export interface HsFrustumDownloadPlan {
  groups: HsGeometryDownloadGroup<HsFrustumDownloadKind>[];
  batchCount: number;
  /** Unique geometry ids over all groups. */
  geometryCount: number;
  /** Untextured mesh batches size themselves by bytes, so progress counts geometries, not batches. */
  adaptive: boolean;
}

/**
 * Splits the missing geometry of a delta into download groups. Textured meshes go first in small fixed
 * batches, then untextured meshes, lines and point clouds. A mesh counts as textured when its instance
 * or its material has a texture; `textureIdByMaterialId` comes from the loaded materials.
 */
export function hsPlanFrustumDownloads(
  scene: HsFrustumSceneState,
  missing: HsFrustumMissingGeometry,
  textureIdByMaterialId: Readonly<Record<string, string>>,
  settings: HsFrustumDownloadSettings
): HsFrustumDownloadPlan {
  const meshes: HsFrustumSceneState['meshes'] = scene.meshes;
  const textured: Set<string> = new Set<string>(
    meshes.renderKeys
      .filter(
        (renderKey: string) =>
          meshes.textureIdByRenderKey[renderKey] !== undefined ||
          textureIdByMaterialId[meshes.materialIdByRenderKey[renderKey]] !== undefined
      )
      .map((renderKey: string) => meshes.geometryIdByRenderKey[renderKey])
  );
  const meshGroups: HsGeometryDownloadGroup<HsFrustumDownloadKind>[] = [
    {
      kind: 'meshes',
      geometryIds: missing.meshes.filter((geometryId: string) => textured.has(geometryId)),
      batchSize: settings.texturedMeshDownloadBatchSize,
      maxBatchSize: settings.texturedMeshDownloadBatchSize,
    },
    {
      kind: 'meshes',
      geometryIds: missing.meshes.filter((geometryId: string) => !textured.has(geometryId)),
      ...(settings.adaptiveMeshBatchBytes !== undefined ? { targetBytes: settings.adaptiveMeshBatchBytes } : {}),
    },
  ];
  const groups: HsGeometryDownloadGroup<HsFrustumDownloadKind>[] = [
    ...meshGroups,
    { kind: 'lines', geometryIds: [...missing.lines] },
    settings.pointCloudBatchBytes === undefined
      ? { kind: 'pointClouds', geometryIds: [...missing.pointClouds], batchSize: settings.pointCloudDownloadBatchSize }
      : {
          kind: 'pointClouds',
          geometryIds: [...missing.pointClouds],
          batchSize: Math.min(settings.pointCloudFirstBatchSize ?? 4, settings.pointCloudDownloadBatchSize),
          maxBatchSize: settings.pointCloudDownloadBatchSize,
          targetBytes: settings.pointCloudBatchBytes,
        },
  ];
  return {
    groups,
    batchCount: hsCountGeometryDownloadBatches(groups, settings.downloadBatchSize),
    geometryCount: groups.reduce(
      (total: number, group: HsGeometryDownloadGroup<HsFrustumDownloadKind>) => total + new Set(group.geometryIds).size,
      0
    ),
    adaptive: settings.adaptiveMeshBatchBytes !== undefined,
  };
}
